import { appendUrlSearchParams } from "../utils/append-url-search-params";
import { NIKE_URL } from "@/constants/url";
import { TimeFrame } from "@/types/chart";
import { z } from "zod";

export type Indicator =
  | "revenue"
  | "eps"
  | "pe"
  | "roe"
  | "roa"
  | "ros"
  | "roic"
  | "valuation"
  | "revenue_yoy"
  | "eps_yoy"
  | "eps_forecast"
  | "revenue_forecast"
  | "forward_pe"
  | "pe_stream";

export type FinancialIndicatorParams = {
  symbol: string;
  indicator: Indicator;
  timeFrame?: TimeFrame;
  fromDate?: string;
  toDate?: string;
  sort?: "asc" | "desc";
} & Record<string, string | number | undefined>;

// Define the schema for the normal response
const dataEntrySchema = z.object({
  time: z.string(),
  value: z.number(),
});

const normalResponseSchema = z.object({
  data: z.record(z.array(dataEntrySchema)),
  message: z.string(),
});

// Define the schema for the forecast response
const forecastDataEntrySchema = z.object({
  date: z.string(),
  value: z.number(),
});

const forecastResponseSchema = z.object({
  data: z.record(
    z.object({
      pastData: z.array(forecastDataEntrySchema),
      maxForecast: z.array(forecastDataEntrySchema),
      avgForecast: z.array(forecastDataEntrySchema),
      minForecast: z.array(forecastDataEntrySchema),
    })
  ),
  message: z.string(),
});

// Define the schema for the PE stream response
const peStreamDataEntrySchema = z.object({
  date: z.string(),
  score: z.number(),
});

const peStreamDataSchema = z.object({
  text: z.string(),
  data: z.array(peStreamDataEntrySchema),
});

const peStreamResponseSchema = z.object({
  data: z.record(
    z.object({
      monthlyAvgPrice: z.array(peStreamDataEntrySchema),
      PE_stream_1: peStreamDataSchema,
      PE_stream_2: peStreamDataSchema,
      PE_stream_3: peStreamDataSchema,
      PE_stream_4: peStreamDataSchema,
      PE_stream_5: peStreamDataSchema,
      PE_stream_6: peStreamDataSchema,
    })
  ),
  message: z.string(),
});

// Define the schema for the response
export const financialIndicatorResponseSchema = z.union([
  normalResponseSchema,
  forecastResponseSchema,
  peStreamResponseSchema,
]);

export type FinancialIndicatorData = z.infer<
  typeof financialIndicatorResponseSchema
>["data"];

export const getFinancialIndicator = async ({
  symbol,
  indicator,
  timeFrame,
  fromDate,
  toDate,
  sort,
}: FinancialIndicatorParams): Promise<FinancialIndicatorData> => {
  const endpoint = `${NIKE_URL}/financial-indicator`;
  const url = new URL(endpoint);
  appendUrlSearchParams({
    url,
    searchParams: {
      symbol,
      indicator,
      ...(timeFrame && { time_frame: timeFrame }),
      ...(fromDate && { from_date: fromDate }),
      ...(toDate && { to_date: toDate }),
      ...(sort && { sort }),
    },
  });

  const res = await fetch(url.toString(), {
    method: "GET",
  });
  if (!res.ok) {
    throw new Error(`API failed (${endpoint})`);
  }
  return financialIndicatorResponseSchema.parse(await res.json()).data;
};
